import type { DataValue } from 'react-apollo'
import { graphql, useQuery } from 'react-apollo'

import { find } from 'lodash'
import { compose, withProps } from 'recompose'

import type { SpendPendingItemsQuery } from 'generated/types'
import {
  InvestmentAccount,
  Portfolio,
  ProductAccountType,
  SelfDirectedPortfolio,
  SelfDirectedPortfolioStatus,
  SpendPendingItemsDocument,
  TierProduct,
  useSpendPendingItemsQuery,
} from 'generated/types'

import {
  AccountDetails,
  AccountState,
  EarlyAccountDetails,
  UserProps,
} from './index'
import edgeCaseVerificationQuery from './queries/subscription-tier-query.gql'

export type EligibilityStatus = {
  eligible: boolean
  status: string
  recurringOn?: boolean
  account?: string
  tier?: { id: string; key: string; includedProducts: TierProduct[] }
}

/**
 * This is a utility helper that parses through the users investment accounts and sorts by open accounts
 * Please note: This is looking at open investment accounts -- does not take into account beneficiaries (or incomplete Early setups. See: {@link https://acorns.atlassian.net/browse/XP-995})
 * @param investmentAccounts investmentAccountsByUserId data set
 * If there are no open accounts, the value is an empty array
 * @returns accounts - Object with properties of each investment account with values being an array which contains data for each account. If array is empty, there are no open accounts.
 */
const sortActiveInvestmentAccounts = (
  investmentAccounts: InvestmentAccount[],
) => {
  const accounts = {
    investAccount: [] as AccountDetails[],
    laterAccounts: [] as AccountDetails[],
    earlyAccounts: [] as EarlyAccountDetails[],
  }
  investmentAccounts?.map((account: InvestmentAccount) => {
    if (account.status === 'OPEN') {
      switch (account.product) {
        case 'INVEST':
          accounts.investAccount.push({
            id: account.id,
            status: account.status,
            balance: account?.currentBalance?.value ?? 0,
            pendingBalance: account.pendingBalance.value,
            recurringInvestmentSettings:
              account.recurringInvestmentSettings as NonNullable<
                InvestmentAccount['recurringInvestmentSettings']
              >,
          })

          break
        case 'EARLY':
          accounts.earlyAccounts.push({
            id: account.id,
            status: account.status,
            balance: account?.currentBalance?.value ?? 0,
            pendingBalance: account.pendingBalance.value,
            beneficiaryId: account.beneficiaryId ?? '',
            recurringInvestmentSettings:
              account.recurringInvestmentSettings as NonNullable<
                InvestmentAccount['recurringInvestmentSettings']
              >,
          })
          break
        case 'LATER':
          accounts.laterAccounts.push({
            id: account.id,
            status: account.status,
            balance: account?.currentBalance?.value ?? 0,
            pendingBalance: account.pendingBalance.value,
            recurringInvestmentSettings:
              account.recurringInvestmentSettings as NonNullable<
                InvestmentAccount['recurringInvestmentSettings']
              >,
          })
          break
      }
    }
  })

  return accounts
}

/** This was repetitive data that was extracted to assist with combined closure/downgrade handling
 * @param statuses Elibibility Statuses from each product/tier check
 * @param userAccountState User Account state data provided by withEdgeCaseProductInfo by passed in from the index file
 * @param currentTier Users current tier
 * @param isCancelSubscription Boolean for whether it is cancel subscription flow or not
 * @returns Object with eligibility boolean, and other meta data if eligibility is false (this is then used in the index file and to render  edge case screens: src/pages/settings/my-subscription/tier-subscription-center/close-account-edge-cases.tsx
 */
const determineCominedClosureEligibility = (
  statuses: any,
  userAccountState: any,
  currentTier: UserProps['currentTier'],
  isCancelSubscription: boolean,
) => {
  const failedEligibility = statuses.find(
    (account: EligibilityStatus) => !account.eligible,
  )
  const hasRecurringOn = isCancelSubscription
    ? userAccountState?.hasRecurringTransferOn
    : userAccountState?.laterAccount?.recurringInvestmentSettings?.amount > 0 ||
      userAccountState?.spendAccount?.hasRecurringTransferOn ||
      userAccountState.earlyAccounts?.find(
        (account: InvestmentAccount) =>
          account?.recurringInvestmentSettings?.amount ?? 0 > 0,
      )
  if (failedEligibility) {
    return {
      eligible: false,
      status: failedEligibility?.status,
      tier: currentTier,
      recurringOn:
        (failedEligibility?.status === 'pending transactions' ||
          failedEligibility?.status === 'pending investments') &&
        hasRecurringOn,
    }
  } else {
    return {
      eligible: true,
    }
  }
}

/**
 * @param userAccountState Provided by HOC withEdgeCaseProductInfo from index file
 * @param product key for what type of closure/downgrade the user is requesting (when moving this to the backend would love to have this as an ENUM)
 * @param currentTier users current tier
 */
export const determineAccountClosureEligibility = (
  userAccountState: AccountState,
  product: string,
  currentTier: UserProps['currentTier'],
) => {
  switch (product) {
    case 'early':
      return earlyAccountClosureHandler(userAccountState, currentTier)
      break
    case 'spend':
      return spendAccountClosureHandler(userAccountState, currentTier)
      break
    case 'later':
      return laterAccountClosureHandler(userAccountState, currentTier)
      break
    case 'SILVER':
      return silverDowngradeHandler(userAccountState, currentTier)
      break
    case 'BRONZE':
      return bronzeDowngradeHandler(userAccountState, currentTier)
    default:
      return fullSubscriptionClosureHandler(userAccountState, currentTier)
  }
}

/** Utility to determine Early closure eligibility */

const earlyAccountClosureHandler = (
  userAccountState: AccountState,
  currentTier: UserProps['currentTier'],
) => {
  if (userAccountState?.earlyAccounts?.length > 0) {
    if (
      userAccountState?.earlyAccounts?.find(
        (account) => account.balance > 0 || account.pendingBalance > 0,
      ) &&
      !userAccountState?.hasFundingSource
    ) {
      return { eligible: false, status: 'link account', tier: currentTier }
    } else if (
      userAccountState?.earlyAccounts?.find(
        (account) => account.balance > 0 || account.pendingBalance > 0,
      ) &&
      !userAccountState.hasVerifiedFS
    ) {
      return {
        eligible: false,
        status: 'nonverified fs',
        tier: currentTier,
      }
    }
  }
  return { eligible: true }
}

/** Utility for determinging Silver Tier downgrade eligibility */
const silverDowngradeHandler = (
  userAccountState: AccountState,
  currentTier: UserProps['currentTier'],
) => {
  const earlyClosureEligibility = earlyAccountClosureHandler(
    userAccountState,
    currentTier,
  )
  return earlyClosureEligibility
}

/** Utility for determining Bronze dowgrade eligibility */
const bronzeDowngradeHandler = (
  userAccountState: AccountState,
  currentTier: UserProps['currentTier'],
) => {
  const eligibilityStatuses = []
  const earlyClosureEligibility = earlyAccountClosureHandler(
    userAccountState,
    currentTier,
  )
  const spendClosureEligibility = spendAccountClosureHandler(
    userAccountState,
    currentTier,
  )
  const laterClosureEligibility = laterAccountClosureHandler(
    userAccountState,
    currentTier,
  )

  eligibilityStatuses.push(
    earlyClosureEligibility,
    spendClosureEligibility,
    laterClosureEligibility,
  )

  return determineCominedClosureEligibility(
    eligibilityStatuses,
    userAccountState,
    currentTier,
    false,
  )
}

/** Utility for determinging Spend closure eligibility */
const spendAccountClosureHandler = (
  userAccountState: any,
  currentTier: UserProps['currentTier'],
) => {
  if (userAccountState?.spendAccount) {
    if (userAccountState?.spendAccount.balance < 0) {
      return { eligible: false, status: 'negative balance' }
    } else if (
      userAccountState?.spendAccount.balance > 0 &&
      !userAccountState?.hasFundingSource
    ) {
      return { eligible: false, status: 'link account' }
    } else if (
      userAccountState?.spendAccount.balance > 0 &&
      !userAccountState?.hasVerifiedFS
    ) {
      return { eligible: false, status: 'nonverified fs' }
    } else if (userAccountState?.spendAccount.hasPendingTransactions) {
      return {
        eligible: false,
        status: 'pending transactions',
        tier: currentTier,
        recurringOn: userAccountState?.spendAccount.hasRecurringTransferOn,
        spendTransferId: userAccountState?.spendAccount.recurringTransferId,
      }
    }
  }
  return { eligible: true }
}

/** Utility for determining Later closure eligibility */
const laterAccountClosureHandler = (
  userAccountState: any,
  currentTier: UserProps['currentTier'],
) => {
  if (userAccountState?.laterAccount) {
    if (userAccountState.laterAccount.pendingBalance > 0) {
      return {
        eligible: false,
        status: 'pending investments',
        tier: currentTier,
        recurringOn:
          userAccountState?.laterAccount?.recurringInvestmentSettings?.amount >
          0,
      }
    } else if (userAccountState?.laterAccount.balance < 0) {
      return { eligible: false, status: 'negative balance', account: 'Later' }
    } else if (
      userAccountState?.laterAccount?.balance > 0 &&
      !userAccountState?.hasFundingSource
    ) {
      return { eligible: false, status: 'link account' }
    } else if (
      userAccountState?.laterAccount?.balance > 0 &&
      !userAccountState?.hasVerifiedFS
    ) {
      return { eligible: false, status: 'nonverified fs' }
    }
  }
  return { eligible: true }
}

/** Utility for determining Invest closure eligibility */
const investClosureHandler = (
  userAccountState: any,
  currentTier: UserProps['currentTier'],
) => {
  if (userAccountState?.investAccount?.pendingBalance > 0) {
    return {
      eligible: false,
      status: 'pending investments',
      tier: currentTier,
      recurringOn:
        userAccountState?.investAccount?.recurringInvestmentSettings?.amount >
        0,
    }
  } else if (userAccountState?.investAccount?.balance < 0) {
    return { eligible: false, status: 'negative balance', account: 'Invest' }
  } else if (
    userAccountState?.investAccount?.balance > 0 &&
    !userAccountState?.hasFundingSource
  ) {
    return { eligible: false, status: 'link account' }
  } else if (
    userAccountState?.investAccount?.balance > 0 &&
    !userAccountState?.hasVerifiedFS
  ) {
    return { eligible: false, status: 'nonverified fs' }
  }
  return { eligible: true }
}

/** @FullSubscriptionClosureHandler Utility for when user is looking to cancel full subscription */
const fullSubscriptionClosureHandler = (
  userAccountState: AccountState,
  currentTier: UserProps['currentTier'],
) => {
  const eligibilityStatuses = []
  const earlyClosureEligibility = earlyAccountClosureHandler(
    userAccountState,
    currentTier,
  )

  const spendClosureEligibility = spendAccountClosureHandler(
    userAccountState,
    currentTier,
  )
  const laterClosureEligibility = laterAccountClosureHandler(
    userAccountState,
    currentTier,
  )
  const investClosureEligibility = investClosureHandler(
    userAccountState,
    currentTier,
  )

  eligibilityStatuses.push(
    earlyClosureEligibility,
    spendClosureEligibility,
    laterClosureEligibility,
    investClosureEligibility,
  )
  return determineCominedClosureEligibility(
    eligibilityStatuses,
    userAccountState,
    currentTier,
    true,
  )
}

/**
 * Prior to entering any of the individual product closure flows, downgrade flow
 * or cancel subscription flow, the account closure eligibility needs to be determined.
 * This HOC provides all relevant product related data needed to determine this eligibility,
 * and also provides account status that aids with rendering the UI of the Subscription Center
 * such as the Menu items in the dropdown & Spend Closed Account Modal
 *
 *
 *
 * Futher documentaiton can be found here: {@link https://acorns.atlassian.net/wiki/spaces/EXP/pages/746029074/Downgrade+Closure+Eligibility+Lambda}.
 */

export const useEdgeCaseProductInfo = () => {
  const { loading, data } = useQuery(edgeCaseVerificationQuery, {
    fetchPolicy: 'network-only',
  })

  const spendAccountId = data?.allBankAccounts?.[0]?.id

  const {
    data: spendPendingData,
    loading: isSpendPendingLoading,
    error: spendPendingError,
  } = useSpendPendingItemsQuery({
    fetchPolicy: 'network-only',
    skip: !spendAccountId,
    variables: {
      accountId: spendAccountId ?? '',
      productAccountType: ProductAccountType.Checking,
    },
  })

  if (!loading && !isSpendPendingLoading) {
    const isVerifiedUser = data?.user?.verified
    const spendAccountStatus = data?.allBankAccounts?.[0]?.status
    const bankCardOrderStatus = data?.bankCardOrder?.cardOrderStatus
    // checks both for created at date and open status to handle closed accounts
    const hasSpendAccount =
      spendAccountStatus !== 'closed' ||
      bankCardOrderStatus !== 'failure' ||
      bankCardOrderStatus !== 'notOrdered'

    const spendBalance = data?.allBankAccounts?.[0]?.balance?.value
    const spendPendingTransactions =
      Boolean(spendPendingError) ||
      (spendPendingData?.allPastItems?.edges?.length ?? 0) > 0
    const spendRecurringTransferOn = data?.recurringTransfers?.[0]?.active
    const spendRecurringTransferId = data?.recurringTransfers?.[0]?.id
    const spendData = {
      active: spendAccountStatus
        ? spendAccountStatus === 'open'
        : bankCardOrderStatus !== 'failure' &&
          bankCardOrderStatus !== 'notOrdered',
      bankCardOrderStatus,
      bankAccountStatus: spendAccountStatus,
      isClosed:
        bankCardOrderStatus === 'failure' || spendAccountStatus === 'closed',
      balance: spendBalance,
      hasRecurringTransferOn: spendRecurringTransferOn,
      recurringTransferId: spendRecurringTransferId,
      hasPendingTransactions: spendPendingTransactions,
    }

    // Check if user has a passions account that isn't closed yet
    const selfDirectedPortfolio = find(data?.investmentAccountsByUserId, {
      portfolios: [{ __typename: 'SelfDirectedPortfolio' }],
    })
    const hasPassions = selfDirectedPortfolio?.portfolios?.some(
      (portfolio: Portfolio | SelfDirectedPortfolio) => {
        return (
          portfolio.__typename === 'SelfDirectedPortfolio' &&
          (portfolio.status === SelfDirectedPortfolioStatus.Open ||
            portfolio.status === SelfDirectedPortfolioStatus.PendingClosure)
        )
      },
    )

    const hasFundingSource = data?.user?.fundingSource !== null
    const hasVerifiedFS = data?.user?.fundingSource?.verified

    const activeAccounts = sortActiveInvestmentAccounts(
      data?.investmentAccountsByUserId,
    )

    const userAccountState = {
      hasFundingSource,
      isVerifiedUser,
      hasVerifiedFS,
      investAccount: activeAccounts?.investAccount[0],
      laterAccount: activeAccounts?.laterAccounts[0],
      spendAccount: hasSpendAccount ? spendData : { bankCardOrderStatus },
      earlyAccounts: activeAccounts?.earlyAccounts,
      hasRecurringTransferOn:
        (activeAccounts?.investAccount[0]?.recurringInvestmentSettings
          ?.amount ?? -1) > 0 ||
        (activeAccounts?.laterAccounts[0]?.recurringInvestmentSettings
          ?.amount ?? -1) > 0 ||
        spendRecurringTransferOn ||
        activeAccounts?.earlyAccounts.find(
          (account) => (account?.recurringInvestmentSettings?.amount ?? -1) > 0,
        ),
      hasPassions,
    }

    return userAccountState
  }
  return loading || isSpendPendingLoading
}

export const withEdgeCaseProductInfo = compose(
  graphql(edgeCaseVerificationQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
  }),
  graphql(SpendPendingItemsDocument, {
    name: 'spendPendingData',
    skip: (props: { data?: { allBankAccounts?: { id: string }[] } }) =>
      !props.data?.allBankAccounts?.[0]?.id,
    options: (props: { data?: { allBankAccounts?: { id: string }[] } }) => ({
      fetchPolicy: 'network-only',
      variables: {
        accountId: props.data?.allBankAccounts?.[0]?.id ?? '',
        productAccountType: ProductAccountType.Checking,
      },
    }),
  }),
  withProps(
    ({
      data,
      spendPendingData,
    }: {
      data: any
      spendPendingData?: DataValue<SpendPendingItemsQuery>
    }) => {
      if (!data.loading && !spendPendingData?.loading) {
        const isVerifiedUser = data?.user?.verified
        const spendAccountStatus = data?.allBankAccounts?.[0]?.status
        const bankCardOrderStatus = data?.bankCardOrder?.cardOrderStatus
        // checks both for created at date and open status to handle closed accounts
        const hasSpendAccount =
          spendAccountStatus !== 'closed' ||
          bankCardOrderStatus !== 'failure' ||
          bankCardOrderStatus !== 'notOrdered'

        const spendBalance = data?.allBankAccounts?.[0]?.balance?.value
        const spendPendingTransactions =
          Boolean(spendPendingData?.error) ||
          (spendPendingData?.allPastItems?.edges?.length ?? 0) > 0
        const spendRecurringTransferOn = data?.recurringTransfers?.[0]?.active
        const spendRecurringTransferId = data?.recurringTransfers?.[0]?.id
        const spendData = {
          active: spendAccountStatus
            ? spendAccountStatus === 'open'
            : bankCardOrderStatus !== 'failure' &&
              bankCardOrderStatus !== 'notOrdered',
          bankCardOrderStatus,
          bankAccountStatus: spendAccountStatus,
          isClosed:
            bankCardOrderStatus === 'failure' ||
            spendAccountStatus === 'closed',
          balance: spendBalance,
          hasRecurringTransferOn: spendRecurringTransferOn,
          recurringTransferId: spendRecurringTransferId,
          hasPendingTransactions: spendPendingTransactions,
        }

        const hasFundingSource = data?.user?.fundingSource !== null
        const hasVerifiedFS = data?.user?.fundingSource?.verified

        const activeAccounts = sortActiveInvestmentAccounts(
          data?.investmentAccountsByUserId,
        )

        const userAccountState = {
          hasFundingSource,
          isVerifiedUser,
          hasVerifiedFS,
          investAccount: activeAccounts?.investAccount[0],
          laterAccount: activeAccounts?.laterAccounts[0],
          spendAccount: hasSpendAccount ? spendData : { bankCardOrderStatus },
          earlyAccounts: activeAccounts?.earlyAccounts,
          hasRecurringTransferOn:
            (activeAccounts?.investAccount[0]?.recurringInvestmentSettings
              ?.amount ?? 0) > 0 ||
            (activeAccounts?.laterAccounts[0]?.recurringInvestmentSettings
              ?.amount ?? 0) > 0 ||
            spendRecurringTransferOn ||
            activeAccounts?.earlyAccounts.find(
              (account) =>
                (account?.recurringInvestmentSettings?.amount ?? 0) > 0,
            ),
        }

        return {
          userAccountState,
        }
      }
      return undefined
    },
  ),
)
